// src/components/common/AppHeader.tsx
import { useState, useRef, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import {
  Menu,
  X,
  Search,
  Bell,
  User,
  Heart,
  Calendar,
  LogOut,
  Settings,
  ChevronDown,
  HelpCircle,
  Award,
  CreditCard,
  LayoutDashboard,
  Briefcase,
  BarChart3,
  Building,
  Shield,
  Home,
  
  Store,
  Users,
  LogIn,
  UserPlus
} from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import toast from 'react-hot-toast';

// Types
interface Notification {
  id: string;
  title: string;
  message: string;
  isRead: boolean;
  createdAt: string;
  type: 'booking' | 'review' | 'payment' | 'system' | 'promotion';
  actionUrl?: string;
}

// Mock Notifications
const MOCK_NOTIFICATIONS: Notification[] = [
  {
    id: '1',
    title: 'Booking Confirmed',
    message: 'Your booking with Aga Khan Pharmacy has been confirmed',
    isRead: false,
    createdAt: '2026-07-19T10:00:00',
    type: 'booking',
    actionUrl: '/customer/bookings/b1',
  },
  {
    id: '2',
    title: 'New Review Response',
    message: 'A provider responded to your review',
    isRead: false,
    createdAt: '2026-07-18T15:30:00',
    type: 'review',
    actionUrl: '/customer/reviews',
  },
  {
    id: '3',
    title: 'Payment Successful',
    message: 'Your payment of KES 3,500 has been processed',
    isRead: true,
    createdAt: '2026-07-18T09:15:00',
    type: 'payment',
    actionUrl: '/customer/payments',
  },
];

// Navigation configuration by role
const getNavConfig = (role: string | undefined) => {
  const baseNav = [
    { label: 'Home', href: '/', icon: <Home className="w-4 h-4" /> },
  ];

  const roleNav: Record<string, Array<{ label: string; href: string; icon: React.ReactNode }>> = {
    customer: [
      { label: 'Dashboard', href: '/customer/dashboard', icon: <LayoutDashboard className="w-4 h-4" /> },
      { label: 'Search', href: '/search', icon: <Search className="w-4 h-4" /> },
      { label: 'Bookings', href: '/customer/bookings', icon: <Calendar className="w-4 h-4" /> },
      { label: 'Favorites', href: '/customer/favorites', icon: <Heart className="w-4 h-4" /> },
    ],
    provider: [
      { label: 'Dashboard', href: '/provider/dashboard', icon: <LayoutDashboard className="w-4 h-4" /> },
      { label: 'Services', href: '/provider/services', icon: <Briefcase className="w-4 h-4" /> },
      { label: 'Bookings', href: '/provider/bookings', icon: <Calendar className="w-4 h-4" /> },
      { label: 'Analytics', href: '/provider/analytics', icon: <BarChart3 className="w-4 h-4" /> },
    ],
    admin: [
      { label: 'Dashboard', href: '/admin/dashboard', icon: <LayoutDashboard className="w-4 h-4" /> },
      { label: 'Users', href: '/admin/users', icon: <Users className="w-4 h-4" /> },
      { label: 'Providers', href: '/admin/providers', icon: <Store className="w-4 h-4" /> },
      { label: 'Bookings', href: '/admin/bookings', icon: <Calendar className="w-4 h-4" /> },
    ],
  };

  if (!role) return baseNav;
  return [...baseNav, ...(roleNav[role] || [])];
};

const getMobileNavItems = (role: string | undefined) => {
  const items = getNavConfig(role);
  
  // Add role-specific items for mobile menu
  const extraItems: Record<string, Array<{ label: string; href: string; icon: React.ReactNode }>> = {
    customer: [
      { label: 'Profile', href: '/customer/profile', icon: <User className="w-4 h-4" /> },
      { label: 'Settings', href: '/customer/settings', icon: <Settings className="w-4 h-4" /> },
    ],
    provider: [
      { label: 'Business Profile', href: '/provider/profile', icon: <Building className="w-4 h-4" /> },
      { label: 'Subscription', href: '/provider/subscription', icon: <CreditCard className="w-4 h-4" /> },
      { label: 'Settings', href: '/provider/settings', icon: <Settings className="w-4 h-4" /> },
    ],
    admin: [
      { label: 'Settings', href: '/admin/settings', icon: <Settings className="w-4 h-4" /> },
    ],
  };

  if (role && extraItems[role]) {
    return [...items, ...extraItems[role]];
  }
  return items;
};

export default function AppHeader() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isAuthenticated, logout } = useAuth();
  
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isUserDropdownOpen, setIsUserDropdownOpen] = useState(false);
  const [isNotificationsOpen, setIsNotificationsOpen] = useState(false);
  const [notifications, setNotifications] = useState(MOCK_NOTIFICATIONS);
  const [scrolled, setScrolled] = useState(false);
  
  const dropdownRef = useRef<HTMLDivElement>(null);
  const notificationRef = useRef<HTMLDivElement>(null);

  const role = user?.role;
  const navItems = getNavConfig(role);
  const mobileNavItems = getMobileNavItems(role);
  const unreadCount = notifications.filter(n => !n.isRead).length;

  // Handle scroll effect
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 10);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close dropdowns on click outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsUserDropdownOpen(false);
      }
      if (notificationRef.current && !notificationRef.current.contains(event.target as Node)) {
        setIsNotificationsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  const handleLogout = async () => {
    await logout();
    toast.success('Logged out successfully');
    navigate('/');
  };

  const handleNotificationClick = (id: string) => {
    const notification = notifications.find(n => n.id === id);
    if (notification) {
      setNotifications(prev =>
        prev.map(n =>
          n.id === id ? { ...n, isRead: true } : n
        )
      );
      if (notification.actionUrl) {
        navigate(notification.actionUrl);
      }
    }
    setIsNotificationsOpen(false);
  };

  const handleMarkAllRead = () => {
    setNotifications(prev =>
      prev.map(n => ({ ...n, isRead: true }))
    );
    toast.success('All notifications marked as read');
  };

  const isActive = (path: string) => {
    if (path === '/') {
      return location.pathname === '/';
    }
    return location.pathname.startsWith(path);
  };

  // Get role display name
  const getRoleDisplayName = (role: string | undefined): string => {
    if (!role) return 'User';
    return role.charAt(0).toUpperCase() + role.slice(1);
  };

  // Get user dropdown items based on role
  const getUserDropdownItems = () => {
    const commonItems = [
      { label: 'Profile', href: `/${role}/profile`, icon: <User className="w-4 h-4" /> },
      { label: 'Settings', href: `/${role}/settings`, icon: <Settings className="w-4 h-4" /> },
    ];

    const roleSpecific: Record<string, Array<{ label: string; href: string; icon: React.ReactNode }>> = {
      customer: [
        { label: 'Dashboard', href: '/customer/dashboard', icon: <LayoutDashboard className="w-4 h-4" /> },
        { label: 'My Bookings', href: '/customer/bookings', icon: <Calendar className="w-4 h-4" /> },
        { label: 'Favorites', href: '/customer/favorites', icon: <Heart className="w-4 h-4" /> },
      ],
      provider: [
        { label: 'Dashboard', href: '/provider/dashboard', icon: <LayoutDashboard className="w-4 h-4" /> },
        { label: 'Business Profile', href: '/provider/profile', icon: <Building className="w-4 h-4" /> },
        { label: 'My Services', href: '/provider/services', icon: <Briefcase className="w-4 h-4" /> },
        { label: 'Subscription', href: '/provider/subscription', icon: <CreditCard className="w-4 h-4" /> },
      ],
      admin: [
        { label: 'Dashboard', href: '/admin/dashboard', icon: <LayoutDashboard className="w-4 h-4" /> },
        { label: 'Users', href: '/admin/users', icon: <Users className="w-4 h-4" /> },
        { label: 'Analytics', href: '/admin/analytics', icon: <BarChart3 className="w-4 h-4" /> },
      ],
    };

    const roleItems = role && roleSpecific[role] ? roleSpecific[role] : [];
    return [...roleItems, ...commonItems];
  };

  // Get role-specific badge color
  const getRoleBadgeColor = (role: string | undefined) => {
    switch(role) {
      case 'customer': return 'bg-blue-50 text-blue-600';
      case 'provider': return 'bg-purple-50 text-purple-600';
      case 'admin': return 'bg-red-50 text-red-600';
      default: return 'bg-gray-50 text-gray-600';
    }
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? 'bg-white/95 backdrop-blur-md shadow-sm border-b border-gray-200'
          : 'bg-white border-b border-gray-100'
      }`}
    >
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link 
            to={isAuthenticated ? `/${role}/dashboard` : '/'} 
            className="flex items-center gap-2 flex-shrink-0"
          >
            <div className="h-9 w-9 rounded-lg bg-primary-500 flex items-center justify-center text-white font-bold text-sm">
              J
            </div>
            <span className="text-xl font-bold text-gray-900 hidden sm:inline">
              Jibu<span className="text-primary-500">24</span>
            </span>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-1">
            {navItems.map((item) => (
              <Link
                key={item.href}
                to={item.href}
                className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                  isActive(item.href)
                    ? 'bg-primary-50 text-primary-600'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                }`}
              >
                {item.icon}
                {item.label}
              </Link>
            ))}
          </nav>

          {/* Right Section */}
          <div className="flex items-center gap-2">
            {isAuthenticated ? (
              <>
                {/* Quick Search - Desktop only */}
                <button
                  onClick={() => navigate('/search')}
                  className="hidden md:flex items-center gap-2 px-3 py-2 rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors text-sm text-gray-500"
                >
                  <Search className="w-4 h-4" />
                  <span className="hidden lg:inline">Search services...</span>
                </button>

                {/* Notifications */}
                <div className="relative" ref={notificationRef}>
                  <button
                    onClick={() => setIsNotificationsOpen(!isNotificationsOpen)}
                    className="relative p-2 rounded-lg hover:bg-gray-100 transition-colors text-gray-600"
                    aria-label="Notifications"
                  >
                    <Bell className="w-5 h-5" />
                    {unreadCount > 0 && (
                      <span className="absolute top-1 right-1 w-4 h-4 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center">
                        {unreadCount}
                      </span>
                    )}
                  </button>

                  {/* Notifications Dropdown */}
                  {isNotificationsOpen && (
                    <div className="absolute right-0 mt-2 w-80 bg-white rounded-xl shadow-lg border border-gray-200 py-2 overflow-hidden z-50">
                      <div className="px-4 py-2 border-b border-gray-100">
                        <div className="flex items-center justify-between">
                          <h3 className="font-semibold text-gray-900">Notifications</h3>
                          {unreadCount > 0 && (
                            <button
                              onClick={handleMarkAllRead}
                              className="text-xs text-primary-600 hover:text-primary-700 font-medium"
                            >
                              Mark all read
                            </button>
                          )}
                        </div>
                      </div>
                      <div className="max-h-96 overflow-y-auto">
                        {notifications.length === 0 ? (
                          <div className="px-4 py-8 text-center text-gray-500 text-sm">
                            <Bell className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                            No notifications
                          </div>
                        ) : (
                          notifications.map((notification) => (
                            <button
                              key={notification.id}
                              onClick={() => handleNotificationClick(notification.id)}
                              className={`w-full text-left px-4 py-3 hover:bg-gray-50 transition-colors border-b border-gray-50 last:border-0 ${
                                !notification.isRead ? 'bg-primary-50/50' : ''
                              }`}
                            >
                              <div className="flex items-start gap-3">
                                <div className="flex-shrink-0 mt-0.5">
                                  {notification.type === 'booking' && (
                                    <Calendar className="w-4 h-4 text-blue-500" />
                                  )}
                                  {notification.type === 'review' && (
                                    <Star className="w-4 h-4 text-yellow-400" />
                                  )}
                                  {notification.type === 'payment' && (
                                    <CreditCard className="w-4 h-4 text-green-500" />
                                  )}
                                  {notification.type === 'system' && (
                                    <Settings className="w-4 h-4 text-purple-500" />
                                  )}
                                  {notification.type === 'promotion' && (
                                    <Award className="w-4 h-4 text-pink-500" />
                                  )}
                                </div>
                                <div className="flex-1 min-w-0">
                                  <p className="text-sm font-medium text-gray-900 truncate">
                                    {notification.title}
                                  </p>
                                  <p className="text-xs text-gray-500 truncate">
                                    {notification.message}
                                  </p>
                                  <p className="text-xs text-gray-400 mt-1">
                                    {new Date(notification.createdAt).toLocaleDateString()} at{' '}
                                    {new Date(notification.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                  </p>
                                </div>
                                {!notification.isRead && (
                                  <span className="w-2 h-2 rounded-full bg-primary-500 flex-shrink-0 mt-1" />
                                )}
                              </div>
                            </button>
                          ))
                        )}
                      </div>
                      {notifications.length > 0 && (
                        <div className="px-4 py-2 border-t border-gray-100">
                          <Link
                            to={`/${role}/notifications`}
                            className="block text-center text-sm text-primary-600 hover:text-primary-700 font-medium"
                            onClick={() => setIsNotificationsOpen(false)}
                          >
                            View all notifications
                          </Link>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* User Dropdown - Desktop only */}
                <div className="relative hidden lg:block" ref={dropdownRef}>
                  <button
                    onClick={() => setIsUserDropdownOpen(!isUserDropdownOpen)}
                    className="flex items-center gap-2 p-1.5 rounded-lg hover:bg-gray-100 transition-colors"
                    aria-label="User menu"
                  >
                    <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center text-primary-600 font-semibold text-sm">
                      {user?.first_name?.charAt(0) || user?.email?.charAt(0) || 'U'}
                    </div>
                    <ChevronDown
                      className={`w-4 h-4 text-gray-400 transition-transform ${
                        isUserDropdownOpen ? 'rotate-180' : ''
                      }`}
                    />
                  </button>

                  {/* User Dropdown Menu */}
                  {isUserDropdownOpen && (
                    <div className="absolute right-0 mt-2 w-64 bg-white rounded-xl shadow-lg border border-gray-200 py-2 overflow-hidden z-50">
                      {/* User Info */}
                      <div className="px-4 py-3 border-b border-gray-100">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center text-primary-600 font-semibold">
                            {user?.first_name?.charAt(0) || user?.email?.charAt(0) || 'U'}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-semibold text-gray-900 truncate">
                              {user?.first_name} {user?.last_name}
                            </p>
                            <p className="text-sm text-gray-500 truncate">{user?.email}</p>
                            <span className={`inline-flex items-center gap-1 mt-1 text-xs font-medium px-2 py-0.5 rounded-full ${getRoleBadgeColor(role)}`}>
                              <Shield className="w-3 h-3" />
                              {getRoleDisplayName(role)}
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* Navigation Items */}
                      <div className="py-1">
                        {getUserDropdownItems().map((item) => (
                          <Link
                            key={item.href}
                            to={item.href}
                            className="flex items-center gap-3 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                            onClick={() => setIsUserDropdownOpen(false)}
                          >
                            {item.icon}
                            {item.label}
                          </Link>
                        ))}
                      </div>

                      {/* Support */}
                      <div className="border-t border-gray-100 py-1">
                        <Link
                          to="/support"
                          className="flex items-center gap-3 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                          onClick={() => setIsUserDropdownOpen(false)}
                        >
                          <HelpCircle className="w-4 h-4" />
                          Help & Support
                        </Link>
                      </div>

                      {/* Logout */}
                      <div className="border-t border-gray-100 py-1">
                        <button
                          onClick={handleLogout}
                          className="flex items-center gap-3 w-full px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors"
                        >
                          <LogOut className="w-4 h-4" />
                          Logout
                        </button>
                      </div>
                    </div>
                  )}
                </div>

                {/* Mobile: User Avatar that opens mobile menu - replaces hamburger */}
                <button
                  onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                  className="lg:hidden relative flex items-center gap-2 p-1.5 rounded-lg hover:bg-gray-100 transition-colors"
                  aria-label="Toggle menu"
                >
                  <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center text-primary-600 font-semibold text-sm">
                    {user?.first_name?.charAt(0) || user?.email?.charAt(0) || 'U'}
                  </div>
                  {unreadCount > 0 && (
                    <span className="absolute top-0 right-0 w-3 h-3 bg-red-500 text-white text-[8px] font-bold rounded-full flex items-center justify-center">
                      {unreadCount}
                    </span>
                  )}
                </button>
              </>
            ) : (
              // Unauthenticated - show login/signup
              <>
                <div className="hidden md:flex items-center gap-2">
                  <Link
                    to="/auth/login"
                    className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900 transition-colors"
                  >
                    Sign In
                  </Link>
                  <Link
                    to="/auth/register"
                    className="px-4 py-2 bg-primary-500 hover:bg-primary-600 text-white text-sm font-medium rounded-lg transition-colors"
                  >
                    Get Started
                  </Link>
                </div>
                
                {/* Mobile hamburger for unauthenticated */}
                <button
                  onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                  className="lg:hidden p-2 rounded-lg hover:bg-gray-100 transition-colors text-gray-600"
                  aria-label="Toggle menu"
                >
                  {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
                </button>
              </>
            )}
          </div>
        </div>

        {/* Mobile Menu - Unified menu for both authenticated and unauthenticated users */}
        {isMobileMenuOpen && (
          <div className="lg:hidden py-4 border-t border-gray-200 animate-slideDown">
            {isAuthenticated ? (
              // Authenticated mobile menu
              <div>
                {/* User Info at top */}
                <div className="px-4 py-3 mb-3 bg-gray-50 rounded-xl">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-full bg-primary-100 flex items-center justify-center text-primary-600 font-semibold text-lg">
                      {user?.first_name?.charAt(0) || user?.email?.charAt(0) || 'U'}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-gray-900">
                        {user?.first_name} {user?.last_name}
                      </p>
                      <p className="text-sm text-gray-500 truncate">{user?.email}</p>
                      <span className={`inline-flex items-center gap-1 mt-0.5 text-xs font-medium px-2 py-0.5 rounded-full ${getRoleBadgeColor(role)}`}>
                        <Shield className="w-3 h-3" />
                        {getRoleDisplayName(role)}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Navigation Items */}
                <nav className="flex flex-col gap-1">
                  {mobileNavItems.map((item) => (
                    <Link
                      key={item.href}
                      to={item.href}
                      className={`flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                        isActive(item.href)
                          ? 'bg-primary-50 text-primary-600'
                          : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                      }`}
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      {item.icon}
                      {item.label}
                    </Link>
                  ))}
                  
                  {/* Divider */}
                  <div className="my-2 border-t border-gray-200" />
                  
                  {/* Support */}
                  <Link
                    to="/support"
                    className="flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm font-medium text-gray-600 hover:bg-gray-50 transition-colors"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    <HelpCircle className="w-4 h-4" />
                    Help & Support
                  </Link>
                  
                  {/* Logout */}
                  <button
                    onClick={handleLogout}
                    className="flex items-center gap-3 w-full px-4 py-2.5 rounded-lg text-sm font-medium text-red-600 hover:bg-red-50 transition-colors mt-1"
                  >
                    <LogOut className="w-4 h-4" />
                    Logout
                  </button>
                </nav>
              </div>
            ) : (
              // Unauthenticated mobile menu
              <nav className="flex flex-col gap-1">
                {navItems.map((item) => (
                  <Link
                    key={item.href}
                    to={item.href}
                    className={`flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                      isActive(item.href)
                        ? 'bg-primary-50 text-primary-600'
                        : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                    }`}
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    {item.icon}
                    {item.label}
                  </Link>
                ))}
                
                <div className="mt-2 pt-2 border-t border-gray-200">
                  <Link
                    to="/auth/login"
                    className="flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm font-medium text-gray-600 hover:bg-gray-50 transition-colors"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    <LogIn className="w-4 h-4" />
                    Sign In
                  </Link>
                  <Link
                    to="/auth/register"
                    className="flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm font-medium text-primary-600 bg-primary-50 hover:bg-primary-100 transition-colors"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    <UserPlus className="w-4 h-4" />
                    Create Account
                  </Link>
                </div>
              </nav>
            )}
          </div>
        )}
      </div>

      {/* Animation CSS */}
      <style>{`
        @keyframes slideDown {
          from {
            opacity: 0;
            transform: translateY(-10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-slideDown {
          animation: slideDown 0.2s ease-out;
        }
      `}</style>
    </header>
  );
}

// Star component
const Star = (props: React.SVGProps<SVGSVGElement>) => (
  <svg
    {...props}
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
  </svg>
);